import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../utils/simpleBookingAPI';

const AdminPanel = ({ onClose }) => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const allBookings = await bookingAPI.getBookings();
      setBookings(allBookings);

      // Calculate stats
      const totalBookings = allBookings.length;
      const totalSeatsBooked = allBookings.length; // Each booking is one seat
      const totalSeatsAvailable = 40 - totalSeatsBooked;
      const occupancyRate = totalSeatsBooked > 0 ? ((totalSeatsBooked / 40) * 100).toFixed(1) : 0;

      setStats({
        totalBookings,
        totalSeatsBooked,
        totalSeatsAvailable,
        occupancyRate
      });
    } catch (error) {
      console.error('Failed to load bookings:', error);
      alert('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    try {
      // Clear all bookings by saving an empty array
      await bookingAPI.saveBookings([]);
      await loadData();
      setShowConfirmClear(false);
      alert('All bookings cleared successfully');
      // Notify parent to refresh
      onClose();
    } catch (error) {
      console.error('Failed to clear bookings:', error);
      alert('Failed to clear bookings');
    }
  };

  const handleRemoveBooking = async (bookingId) => {
    if (window.confirm('Are you sure you want to remove this booking?')) {
      try {
        // Remove the specific booking
        const updatedBookings = bookings.filter(booking => booking.id !== bookingId);
        await bookingAPI.saveBookings(updatedBookings);
        await loadData();
        alert('Booking removed successfully');
        // Notify parent to refresh
        onClose();
      } catch (error) {
        console.error('Failed to remove booking:', error);
        alert('Failed to remove booking');
      }
    }
  };

  const handleExport = () => {
    const data = JSON.stringify({ bookings, exportDate: new Date().toISOString() }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `slc-bookings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = JSON.parse(e.target.result);
          const importedBookings = data.bookings || data; // Handle different formats

          if (Array.isArray(importedBookings)) {
            await bookingAPI.saveBookings(importedBookings);
            await loadData();
            alert('Bookings imported successfully');
            onClose(); // Refresh parent
          } else {
            alert('Invalid file format');
          }
        } catch (error) {
          console.error('Failed to import bookings:', error);
          alert('Failed to import bookings');
        }
      };
      reader.readAsText(file);
    }
  };

  if (loading) {
    return (
      <div className="admin-panel-overlay">
        <div className="admin-panel">
          <div className="admin-header">
            <h2>🛠️ Admin Panel</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel-overlay">
      <div className="admin-panel">
        <div className="admin-header">
          <h2>🛠️ Admin Panel</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="admin-stats">
          <h3>📊 Booking Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Total Bookings:</span>
              <span className="stat-value">{stats.totalBookings}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Seats Booked:</span>
              <span className="stat-value">{stats.totalSeatsBooked}/40</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Occupancy Rate:</span>
              <span className="stat-value">{stats.occupancyRate}%</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Available Seats:</span>
              <span className="stat-value">{stats.totalSeatsAvailable}</span>
            </div>
          </div>
        </div>

        <div className="admin-actions">
          <h3>🔧 Actions</h3>
          <div className="action-buttons">
            <button onClick={handleExport} className="export-btn">
              📥 Export Bookings
            </button>
            <label className="import-btn">
              📤 Import Bookings
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                style={{ display: 'none' }}
              />
            </label>
            <button
              onClick={() => setShowConfirmClear(true)}
              className="clear-btn"
            >
              🗑️ Clear All Bookings
            </button>
          </div>
        </div>

        {showConfirmClear && (
          <div className="confirm-dialog">
            <p>⚠️ Are you sure you want to clear ALL bookings? This cannot be undone!</p>
            <div className="confirm-buttons">
              <button onClick={handleClearAll} className="confirm-yes">Yes, Clear All</button>
              <button onClick={() => setShowConfirmClear(false)} className="confirm-no">Cancel</button>
            </div>
          </div>
        )}

        <div className="admin-bookings">
          <h3>📋 All Bookings ({bookings.length})</h3>
          {bookings.length === 0 ? (
            <p className="no-bookings">No bookings found</p>
          ) : (
            <div className="bookings-list">
              {bookings.map((booking) => (
                <div key={booking.id} className="booking-item">
                  <div className="booking-info">
                    <div className="booking-name">
                      <strong>{booking.name}</strong>
                    </div>
                    <div className="booking-details">
                      <span>📞 {booking.phone}</span>
                      <span>🪑 {booking.seatId}</span>
                      {booking.avatar && (
                        <span>👤 {booking.avatar.charAt(0).toUpperCase() + booking.avatar.slice(1)}</span>
                      )}
                      <span>📅 {new Date(booking.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveBooking(booking.id)}
                    className="remove-booking-btn"
                    title="Remove booking"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
